import React, { useState } from "react";
import { LayoutDashboard } from "lucide-react";
import { cn } from "../../../lib/utils";
import { WidgetCard } from "./ReportWidgets";

// Static class strings (so Tailwind's JIT keeps them) for the 3 widget sizes
// within a 6-column grid: small = 1/3 row, medium = 1/2 row, large = full row.
const SIZE_CLASSES = {
  sm: "col-span-2 sm:col-span-2",
  md: "col-span-2 sm:col-span-3",
  lg: "col-span-2 sm:col-span-6",
};

const METRIC_MIME = "application/x-mb-metric";
const WIDGET_MIME = "application/x-mb-widget";

export default function ReportCanvas({ widgets, onAddMetric, onRemove, onResize, onReorder }) {
  const [dragId, setDragId] = useState(null);

  const handleDropOn = (e, targetIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const metricId = e.dataTransfer.getData(METRIC_MIME);
    const widgetId = e.dataTransfer.getData(WIDGET_MIME);
    if (metricId) {
      onAddMetric(metricId, targetIndex);
    } else if (widgetId) {
      onReorder(widgetId, targetIndex);
    }
    setDragId(null);
  };

  const handleRootDrop = (e) => {
    e.preventDefault();
    const metricId = e.dataTransfer.getData(METRIC_MIME);
    const widgetId = e.dataTransfer.getData(WIDGET_MIME);
    if (metricId) onAddMetric(metricId, widgets.length);
    else if (widgetId) onReorder(widgetId, widgets.length);
    setDragId(null);
  };

  if (!widgets.length) {
    return (
      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleRootDrop}
        className="flex min-h-[320px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-8 text-center"
      >
        <LayoutDashboard className="h-8 w-8 text-muted-foreground" />
        <p className="text-sm font-medium text-foreground">Build your report</p>
        <p className="max-w-xs text-xs text-muted-foreground">
          Drag metrics from the palette onto this canvas, or click the + on any metric to add it.
        </p>
      </div>
    );
  }

  return (
    <div
      onDragOver={(e) => e.preventDefault()}
      onDrop={handleRootDrop}
      className="grid grid-cols-2 gap-4 sm:grid-cols-6"
    >
      {widgets.map((widget, index) => (
        <div key={widget.instanceId} className={cn(SIZE_CLASSES[widget.size] || SIZE_CLASSES.md)}>
          <WidgetCard
            widget={widget}
            dragging={dragId === widget.instanceId}
            onRemove={() => onRemove(widget.instanceId)}
            onResize={() => onResize(widget.instanceId)}
            onDragStartHandle={(e) => {
              e.dataTransfer.setData(WIDGET_MIME, widget.instanceId);
              e.dataTransfer.effectAllowed = "move";
              setDragId(widget.instanceId);
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDropOn(e, index)}
          />
        </div>
      ))}
    </div>
  );
}
